/* Copyright (c) 2020-present CloudIO Inc */

import { SmartSearchAttribute } from 'componentregistry';
import {
  BOOLEAN_OPS,
  BOOLEAN_OPS_KEY_TYPE,
  DATE_OPS,
  DATE_OPS_KEY_TYPE,
  MULTIPLE_DATE_OPS,
  MULTIPLE_NUMBER_OPS,
  MULTIPLE_SELECT_OPS,
  MULTIPLE_STRING_OPS,
  NUMBER_OPS,
  NUMBER_OPS_KEY_TYPE,
  SELECT_OPS,
  STRING_OPS,
  STRING_OPS_KEY_TYPE,
  YN_TF_OPS,
  YN_TF_OPS_KEY_TYPE,
} from '@cloudio-saas/datasource-types';
import { ClientNonNestedFilter } from 'components/SmartSearch/types';
import { AttributeDataType } from 'componentregistry/components/SmartSearch';

export const hasBooleanEditor = (op: BOOLEAN_OPS_KEY_TYPE): boolean => {
  if (['empty', 'notEmpty'].includes(op)) {
    return false;
  }
  return true;
};

export const hasYNTFEditor = (op: YN_TF_OPS_KEY_TYPE): boolean => {
  if (['empty', 'notEmpty'].includes(op)) {
    return false;
  }
  return true;
};

export const hasTextEditor = (op: STRING_OPS_KEY_TYPE): boolean => {
  if (['empty', 'notEmpty'].includes(op)) {
    return false;
  }
  return true;
};

export const hasNumberEditor = (op: NUMBER_OPS_KEY_TYPE): boolean => {
  switch (op) {
    case 'null':
    case 'notNull':
      return false;
    default:
      return true;
  }
};

export const hasDateEditor = (op: DATE_OPS_KEY_TYPE): boolean => {
  switch (op) {
    case 'empty':
    case 'notEmpty':
    case 'today':
    case 'tomorrow':
    case 'yesterday':
    case 'last7days':
    case 'last14days':
    case 'last28days':
    case 'thisWeek':
    case 'thisMonth':
    case 'thisQuarter':
    case 'thisYear':
      return false;
    default:
      return true;
  }
};

export type SELECT_OPS_KEY_TYPE =
  | keyof typeof SELECT_OPS
  | keyof typeof MULTIPLE_SELECT_OPS;

export const hasSelectEditor = (op: SELECT_OPS_KEY_TYPE): boolean => {
  switch (op) {
    case 'empty':
    case 'notEmpty':
      return false;
    default:
      return true;
  }
};

export type StringOperator = {
  value: keyof typeof STRING_OPS;
  label: string;
};

export type MultiStringOperator = {
  value: keyof typeof MULTIPLE_STRING_OPS;
  label: string;
};

export type DateOperator = {
  value: keyof typeof DATE_OPS;
  label: string;
};

export type MultipleDateOperator = {
  value: keyof typeof MULTIPLE_DATE_OPS;
  label: string;
};

export type NumberOperator = {
  value: keyof typeof NUMBER_OPS;
  label: string;
};

export type MultipleNumberOperator = {
  value: keyof typeof MULTIPLE_NUMBER_OPS;
  label: string;
};

export type SelectOperator = {
  value: keyof typeof SELECT_OPS;
  label: string;
};

export type MutltipleSelectOperator = {
  value: keyof typeof MULTIPLE_SELECT_OPS;
  label: string;
};

export type Operator =
  | StringOperator
  | DateOperator
  | NumberOperator
  | SelectOperator
  | MultiStringOperator
  | MultipleDateOperator
  | MultipleNumberOperator
  | MutltipleSelectOperator;

export const allDateOps: Record<DATE_OPS_KEY_TYPE, string> = {
  ...DATE_OPS,
  ...MULTIPLE_DATE_OPS,
};
export const allStringOps: Record<STRING_OPS_KEY_TYPE, string> = {
  ...STRING_OPS,
  ...MULTIPLE_STRING_OPS,
};
export const allNumberOps: Record<NUMBER_OPS_KEY_TYPE, string> = {
  ...NUMBER_OPS,
  ...MULTIPLE_NUMBER_OPS,
};
export const allSelectOps = { ...SELECT_OPS, ...MULTIPLE_SELECT_OPS };
export const allBooleanOps = { ...BOOLEAN_OPS };
export const allYNTFOps = { ...YN_TF_OPS };

export const BOOLEAN_OPERATORS = (
  Object.keys(allBooleanOps) as BOOLEAN_OPS_KEY_TYPE[]
).map((key) => ({
  value: key,
  label: allBooleanOps[key],
}));

export const DATE_OPERATORS = (
  Object.keys(allDateOps) as DATE_OPS_KEY_TYPE[]
).map((key) => ({
  value: key,
  label: allDateOps[key],
}));

export const STRING_OPERATORS = (
  Object.keys(allStringOps) as STRING_OPS_KEY_TYPE[]
).map((key) => ({
  value: key,
  label: allStringOps[key],
}));

export const NUMBER_OPERATORS = (
  Object.keys(allNumberOps) as NUMBER_OPS_KEY_TYPE[]
).map((key) => ({
  value: key,
  label: allNumberOps[key],
}));

export const SELECT_OPERATORS = (
  Object.keys(allSelectOps) as SELECT_OPS_KEY_TYPE[]
).map((key) => ({
  value: key,
  label: allSelectOps[key],
}));

export const YN_TF_OPERATORS = (
  Object.keys(allYNTFOps) as YN_TF_OPS_KEY_TYPE[]
).map((key) => ({
  value: key,
  label: allYNTFOps[key],
}));

export const operatorsArray = {
  Text: STRING_OPERATORS,
  Number: NUMBER_OPERATORS,
  Date: DATE_OPERATORS,
  Select: SELECT_OPERATORS,
  Lookup: SELECT_OPERATORS,
  Boolean: BOOLEAN_OPERATORS,
  YN: YN_TF_OPERATORS,
  TF: YN_TF_OPERATORS,
  JSON: STRING_OPERATORS, // TODO
};
export const getOperatorsArray = (type: AttributeDataType) => {
  return operatorsArray[type] as Operator[];
};

export const getDefaultOperator = (
  smartSearchAttribute: SmartSearchAttribute.SmartSearchAttribute,
) => {
  return smartSearchAttribute.defaultOperator;
};

export const getOperator = (type: AttributeDataType, operatorValue: string) => {
  const operatorArr = operatorsArray[type] as Operator[];
  const operator = operatorArr.find((op) => op.value === operatorValue);
  return operator;
};

export const hasEditor = (entry: ClientNonNestedFilter): boolean => {
  if (entry?.name && entry.operator) {
    switch (entry.dataType) {
      case 'Text':
        return hasTextEditor(entry.operator);
      case 'Date':
        return hasDateEditor(entry.operator);
      case 'Number':
        return hasNumberEditor(entry.operator);
      case 'Select':
      case 'Lookup':
        return hasSelectEditor(entry.operator);
      case 'Boolean':
        return hasBooleanEditor(entry.operator);
      case 'YN':
        return hasYNTFEditor(entry.operator);
      case 'TF':
        return hasYNTFEditor(entry.operator);
      default:
        return false;
    }
  }
  return false;
};
